/*
 * Field Mode's outbox: sends the specimens the camera finished, one at a
 * time and in the order they were finished, whenever there is a connection.
 *
 * Each batch (utils/fieldBatches.js) goes through these steps, each needing
 * what the one before made:
 *
 *   mint the code -> create the collection -> upload the photos
 *     -> tie each Z-stack's photos together -> upload the voice notes
 *     -> import the thermal files -> annotate the photos (one PUT each)
 *     -> submit -> ask for the handoff
 *
 * and what a step made is saved on the batch before the next one starts, so
 * a closed tab, a flat battery or a week without signal resumes exactly where
 * it stopped. One runner per page, started by Field Mode and left running, so
 * a specimen keeps going out while its collector works elsewhere in the app.
 *
 * ONE SENDER, HOWEVER MANY TABS.
 *
 * The app open in two tabs (or the installed app beside a browser tab) is two
 * runners, and both hear the connection come back. Two runners sending one
 * batch would mint two codes -- which can never be deleted -- and create two
 * collections. So a pass holds the browser's lock for this person's outbox
 * (navigator.locks) while it sends, and a tab that cannot have it leaves the
 * work to the one that does. Where there is no lock API, each batch is
 * CLAIMED before it is sent (`sendingBy`, `leaseUntil`, in one read-and-write
 * of the store) and a batch another tab holds is left alone until its lease
 * runs out. The batch is read afresh as it is claimed: whatever its collector
 * changed meanwhile is what goes.
 *
 * NOT AT ONCE.
 *
 * A camera batch waits a minute after New specimen (`holdUntil`), and any
 * batch waits while its editor is open: the label on the bag, or "only a
 * sighting", can still be said before a code is minted that would contradict
 * it.
 *
 * THE CODE A SPECIMEN CARRIES.
 *
 * A label typed or scanned in the details wins and nothing is minted; a
 * sighting with nothing collected gets no code; nor does a specimen
 * photographed by someone who may not register specimens. Otherwise
 * POST /specimens mints one and the collection is created with it as its tag
 * (the server links the two: link_origin). The minted code is kept on the
 * batch, so a retry never mints a second one. A mint the server REFUSES (a
 * 4xx of the route's own) does not stop the specimen: it is saved without a
 * code. A mint nobody answered, or answered with its own trouble (5xx, a
 * gateway timeout), may have landed: it is asked again, word for word.
 *
 * WHEN IT WAS FOUND.
 *
 * A specimen is dated by the moment its first photo was taken (`startedAt`),
 * never by when a request happened to run -- and never later than now. The
 * code's date and time and the collection's collection_datetime both say that
 * moment, whether the batch goes at once or a week later. The mint names no
 * mode, so the server registers one more than a day old as retroactive
 * instead of refusing it as not on site.
 *
 * ONE IDEMPOTENCY KEY PER REQUEST THAT MAY HAVE LANDED.
 *
 * The keys are kept on the batch, so a retry after a timeout is answered once
 * (idempotency.py). The server keeps every answer below 500 under its key, so
 * a key kept after a refusal would get that refusal back on every later try,
 * and after any correction a 422. So a create the route refused (nothing was
 * written) gives its key up, and the next try is a new request. Every body
 * sent under a key is kept with it (`sentBodies`), the mint word for word:
 * a request that landed but whose answer never reached the phone is asked
 * again in its own words and gets that answer back, never a second specimen.
 * What was corrected since is then sent as corrections of that record (or,
 * for what makes up its code, said not to have been applied).
 *
 * Each photo is registered under a key of its own too, and the file is put in
 * storage once: a registration whose answer was lost is not a second photo.
 *
 * NOTHING ANSWERED IS NOT A REFUSAL.
 *
 * A request that reaches nobody leaves the batch pending, photos and all, and
 * it goes when there is a connection. The server saying no is shown as a
 * refusal: the batch waits for its collector (`attention`) rather than being
 * sent some other way around the endpoint that said no. The server's own
 * trouble is tried again, and after a few failures in a row waits a while
 * (attention 'server') before it is tried again by itself.
 *
 * NEVER WITH A TOKEN THAT HAS RUN OUT.
 *
 * utils/api.js signs the person out on any 401, which would throw a collector
 * out of the camera mid-specimen. So every request is preceded by a look at
 * the token's expiry -- a photo, which can take minutes on a field uplink,
 * needs PHOTO_MARGIN_MS of it -- and is sent with `keepSession`, so a 401 that
 * comes anyway stops the outbox rather than the person. A batch whose token
 * has ended waits for the next sign-in. Only the signed-in person's batches
 * are ever sent.
 */
import api from './api';
import { uploadOnePhoto } from './uploadPhotos';
import { uploadAudioNote } from './audioNotes';
import { uploadThermal } from './thermal/upload';
import { bundleBody } from './zstack/bundle';
import { isUnreachable, newUuid } from './collectionRecord';
import { can } from './permissions';
import { localIsoWithOffset, normaliseTag, offsetMinutes } from './specimenCode';
import { backoffMs } from './syncEngine';
import { fieldStore, leaseActive } from './fieldBatches';
import { thumbnailOf } from './fieldFrame';
import { getApiBase } from './serverConfig';
import { fetchWeatherWindow } from './weather/openMeteo';
import { handoffBody } from './handoffs';
import { isExecutive } from './viewAs';
import { annotatedPhotoIds, annotationBody } from './fieldAnnotations';

/** How long a camera batch with no place waits for the camera to find one. */
export const LOCATION_WAIT_MS = 2 * 60 * 1000;
/** How long a finished camera batch waits before it is sent (holdUntil). */
export const CAMERA_HOLD_MS = 60 * 1000;
/** How long an open editor holds its batch back from the outbox. */
export const EDIT_HOLD_MS = 10 * 60 * 1000;
/** Passes over a batch's photos before its collector is asked. */
export const PHOTO_PASSES = 3;
/** Server errors (5xx) in a row before the batch waits a while. */
export const SERVER_PASSES = 5;
/** How long a batch the server kept failing on waits before it is tried
 *  again by itself (a connection coming back tries it at once). */
export const SERVER_REQUEUE_MS = 5 * 60 * 1000;
/** How long a claim on a batch lasts without news from its sender. */
export const LEASE_MS = 5 * 60 * 1000;
/** The browser lock one tab holds while it sends this person's batches. */
export const LOCK_NAME = 'pitriot-field-outbox';
/** Token time a photo needs: presign, a 1-2 MB upload on a slow uplink, and
 *  the registration, all before it runs out. */
export const PHOTO_MARGIN_MS = 5 * 60 * 1000;
// A token this close to its end counts as ended: a request sent now could
// arrive after it.
const EXPIRY_MARGIN_MS = 30 * 1000;
// Another tab is sending: look again in a while, in case it stops.
const ELSEWHERE_RETRY_MS = 15 * 1000;
// A 426: the app and the server are on different versions. When a newer app is
// published the page reloads itself, once (utils/versionSkew.js); otherwise THIS
// page is the newest and the server is mid-deploy (~10-25 minutes after a merge),
// and nothing reloads -- so try again every few minutes until it has caught up.
// Coming back online or to the page tries sooner.
const OUTDATED_RETRY_MS = 3 * 60 * 1000;
const TOKEN_KEY = 'mb_token';
// Every request: a 401 in the background stops the outbox, not the person.
const REQUEST = { keepSession: true };

function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Who is signed in, while their token still has time on it: {userId, roles,
 *  exp, viewAs}, else null. Read afresh each time: a sign-out or a new sign-in
 *  takes effect on the next request. `viewAs` is the role an admin is viewing
 *  the app as (utils/viewAs.js), else null: the screens may read with it, but
 *  nothing is sent -- the token is read-only, and the batches waiting are the
 *  admin's own, sent as the admin once they Exit. */
export function readSession(storage = globalThis.localStorage, now = Date.now()) {
  let token = null;
  try { token = storage?.getItem(TOKEN_KEY); } catch { return null; }
  const claims = token ? parseJwt(token) : null;
  if (!claims || !claims.user_id || !(Number(claims.exp) * 1000 > now + EXPIRY_MARGIN_MS)) return null;
  return {
    userId: String(claims.user_id),
    roles: claims.roles || [],
    exp: Number(claims.exp),
    // An executive's account (0098) may only read too: it sends nothing.
    viewAs: claims.vas ? ((claims.roles || [])[0] || null) : (isExecutive(claims) ? claims.roles[0] : null),
  };
}

export class SessionEnded extends Error {}
class Gone extends Error {}
/** The record this batch made is not on the server any more: deleted there,
 *  or never on this one (made through another server address). */
class RecordGone extends Error {
  constructor(gone) {
    super('record gone');
    this.gone = gone;
  }
}

/** What GET /collections/{id}'s 404 says of a record that is not there
 *  (routes/collections.py): deleted -- and when -- to whoever could read it,
 *  else just not found. An older server says neither: `known` false. */
export function goneOf(error, at = new Date()) {
  const data = error?.response?.data || {};
  return {
    deleted: data.code === 'collection_deleted',
    deletedAt: data.deleted_at || null,
    known: data.code === 'collection_deleted' || data.code === 'collection_not_found',
    at: at.toISOString(),
  };
}

/* After the record was made, a refusal that may mean it is no longer there:
 * an owner's own deleted record answers PUT with 403 not_your_collection,
 * submit and the AI link with 404, a photo with 404 record_not_found. */
const mayBeGone = status => status === 403 || status === 404;
/** Every body sent under the create's key was answered 422: the key holds a
 *  request this phone has no copy of. Nothing is concluded, and nothing new
 *  is created around it. */
class KeyLost extends Error {}

/* When the specimen was found: the moment its first photo was taken, but
 * never later than now -- a clock set back since would otherwise date it in
 * the future, which the server refuses. */
export function captureMoment(batch, now = new Date()) {
  const began = batch?.startedAt ? new Date(batch.startedAt) : null;
  return began && Number.isFinite(began.getTime()) && began.getTime() <= now.getTime() ? began : now;
}

/* POST /specimens: the moment and the place. No mode (see the header). */
export const mintBody = (batch, moment) => ({
  collected_at: localIsoWithOffset(moment),
  utc_offset_minutes: offsetMinutes(moment),
  gps_lat: Number(batch.location.lat),
  gps_lng: Number(batch.location.lng),
  location_name: (batch.region || '').trim() || undefined,
  taxon_name: (batch.details?.taxon_name || '').trim() || undefined,
});

const text = v => String(v ?? '').trim() || undefined;

/* POST /collections. Blank is not a value: substrate is an ENUM, and '' is
 * not one of its members. */
export const createBody = (batch, code, collectedAt) => {
  const d = batch.details || {};
  return {
    qr_uid: code || undefined,
    collection_datetime: collectedAt,
    gps_lat: batch.location?.lat,
    gps_lng: batch.location?.lng,
    region: text(batch.region),
    substrate: d.substrate || undefined,
    host_species: text(d.host_species),
    // The collector's own name for the find (0110).
    given_name: text(d.given_name),
    current_taxon_name: text(d.taxon_name),
    // The same 'unknown' bypass Collections.js and Survey.js use; it
    // satisfies submit's validation with no special case.
    current_rank: d.rank || 'unknown',
    current_confidence: d.confidence || undefined,
    general_notes: text(d.general_notes),
    // The mission the batch was taken for; the server drops it (never refuses
    // the record) when the person no longer holds that card.
    mission_id: batch.missionId || undefined,
    // The observation the batch documents (the map's target, utils/
    // activeTarget.js); the server drops it, never the record, when that
    // observation is not one the person may see.
    target_sample_id: batch.targetId || undefined,
  };
};

/* The link of the AI's answer to the record it became: an acceptance goes
 * only while the name the person accepted is still the one on the batch. */
export function aiLinkBody(batch, sampleId) {
  const accepted = batch.ai?.accepted;
  const name = text(batch.details?.taxon_name);
  const kept = accepted && name && name.toLowerCase() === String(accepted.taxon_name || '').trim().toLowerCase();
  return {
    sample_id: sampleId,
    ...(kept ? { accept: { taxon_name: accepted.taxon_name, rank: accepted.rank,
      confidence: batch.details?.confidence || 'uncertain' } } : {}),
  };
}

// As the server compares them: the JSON body, undefined members left out.
const sameRequest = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* The idempotency layer's own "the first request with this key is still
 * running": a 409 with no `code`, unlike the route's. That request may yet
 * land, so the key stays and nothing is concluded from it. */
export const stillRunning = error => error?.response?.status === 409 && !error.response.data?.code;

/* Answers that are not the route's verdict on this request: the layer's own
 * (422 for a key holding a different request, the in-flight 409), a session
 * that ended (401), a server this app is too old for (426), being asked to
 * slow down (429). None of them says the request was refused for what it
 * carries. */
const notAVerdict = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 422 || status === 426 || status === 429 || stillRunning(error);
};

/* A refusal of what the request carried, which wrote nothing: after it the
 * batch's idempotency key has to go -- the server keeps every answer below 500
 * under it -- and the next try is a new request. */
export function refusedOutright(error) {
  const status = error?.response?.status;
  if (!status || status < 400 || status >= 500) return false;
  return !notAVerdict(error);
}

/* A mint whose answer says nothing about whether it ran: nobody answered, the
 * server's own trouble (a 504 from the gateway while the server went on), or
 * one of the answers above. It is asked again with its own key and body;
 * only the route's own refusal lets the specimen go without a code. */
const mintUnsettled = error => isUnreachable(error) || error instanceof SessionEnded
  || error?.response?.status >= 500 || notAVerdict(error);

const unreachableError = () => Object.assign(new Error('Network Error'), { response: undefined });

/** When and where one photo was taken, as POST /photos takes it (0053).
 *  Rounded to the columns; an accuracy past the column's range says nothing
 *  useful and would refuse the photo. */
export function captureFields(photo) {
  const out = {};
  if (photo.takenAt) out.captured_at = photo.takenAt;
  if (Number.isFinite(photo.lat) && Number.isFinite(photo.lng)) {
    out.capture_lat = Number(photo.lat.toFixed(6));
    out.capture_lng = Number(photo.lng.toFixed(6));
    if (Number.isFinite(photo.accuracy_m) && photo.accuracy_m >= 0 && photo.accuracy_m <= 100000) {
      out.capture_accuracy_m = Math.round(photo.accuracy_m * 10) / 10;
    }
  }
  // Which way the camera faced (0090): one decimal, never rounded up to 360.
  if (Number.isFinite(photo.heading) && photo.heading >= 0 && photo.heading < 360) {
    out.capture_heading_deg = Math.min(Math.round(photo.heading * 10) / 10, 359.9);
  }
  return out;
}

/* POST /photos refusing a photo's tags rather than the photo. The server holds
 * captured_at to its own clock, so a phone whose clock runs fast would lose
 * every photograph it took over a time stamp: such a photo goes again without
 * its tags (the batch's own time and place still date and place the record). */
export const TAG_REFUSALS = new Set(['invalid_captured_at', 'invalid_coordinates', 'invalid_accuracy',
  'invalid_heading']);

/* The answers that settle a batch's weather for good (the Weather step). */
export const WEATHER_SETTLED = new Set([400, 403, 404, 409, 422]);

/** A camera frame is a Blob with no name; presign wants a filename. */
export function asFile(blob, photo, index) {
  if (typeof File !== 'undefined' && blob instanceof File && blob.name) return blob;
  return new File([blob], `${photo.view}-${index + 1}.jpg`, { type: blob.type || 'image/jpeg' });
}

/** A data URL (thumbnailOf's answer) as a Blob, or null. */
export function blobOfDataUrl(url) {
  const match = /^data:([^;,]+)?(;base64)?,(.*)$/.exec(String(url || ''));
  if (!match) return null;
  try {
    const raw = match[2] ? atob(match[3]) : decodeURIComponent(match[3]);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
    return new Blob([bytes], { type: match[1] || 'image/jpeg' });
  } catch {
    return null;
  }
}

/** Where an earlier attempt put a photo (and its small copy) in storage. */
export const storedOf = photo => ({
  s3_key: photo.s3_key, s3_url: photo.s3_url, ...(photo.thumb_key ? { thumb_key: photo.thumb_key } : {}),
});

/** The idempotency key one photo's registration goes under. */
export const photoKey = (createKey, photoId, tagged = true) =>
  `${createKey}-photo-${photoId}${tagged ? '' : '-untagged'}`;

/** The idempotency key one voice note's registration goes under; `timed`
 *  false for the one sent again without the moment it was recorded. */
export const audioKey = (createKey, noteId, timed = true) =>
  `${createKey}-audio-${noteId}${timed ? '' : '-untimed'}`;

/* Every body sent under the create's key so far: the batch's list, and the
 * body an older version of this file (or the quick-capture screen) kept. */
function sentUnder(batch) {
  const all = [...(batch.sentBodies || []), ...(batch.createBody ? [batch.createBody] : [])];
  return all.filter((b, i) => all.findIndex(o => sameRequest(o, b)) === i);
}

// What a record's owner may still correct once it exists (collections.py,
// FIELD_DETAIL_FIELDS), what goes as a new identification, and what makes up
// its specimen code and so stays as it was made.
const DETAIL_KEYS = ['general_notes', 'host_species', 'substrate', 'given_name'];
const IDENT_KEYS = ['current_taxon_name', 'current_rank', 'current_confidence'];
const FIXED_KEYS = ['collection_datetime', 'gps_lat', 'gps_lng', 'qr_uid', 'region'];
const valueOf = v => (v === undefined || v === '' ? null : v);

/* What the collector changed after the request that made the record was
 * sent: details to correct on it, an identification to add, and what could
 * not follow (its code, date and place). */
export function editsBetween(sent, wanted) {
  const changes = {};
  for (const k of DETAIL_KEYS) {
    if (valueOf(sent[k]) !== valueOf(wanted[k])) changes[k] = valueOf(wanted[k]);
  }
  const notApplied = FIXED_KEYS.filter(k => String(valueOf(sent[k]) ?? '') !== String(valueOf(wanted[k]) ?? ''));
  let ident = null;
  if (IDENT_KEYS.some(k => valueOf(sent[k]) !== valueOf(wanted[k]))) {
    if (valueOf(wanted.current_taxon_name) && valueOf(wanted.current_rank)) {
      ident = {
        taxon_name: wanted.current_taxon_name,
        taxonomic_rank: wanted.current_rank,
        confidence: wanted.current_confidence || 'uncertain',
        identification_method: 'field',
      };
    } else {
      // An identification needs a name; "I could not identify it" after one
      // was given is for the collector to say on the record.
      notApplied.push('current_taxon_name');
    }
  }
  return { changes: Object.keys(changes).length ? changes : null, ident, notApplied };
}

export function createOutbox({
  store = fieldStore(),
  client = api,
  uploadOne = uploadOnePhoto,
  // One voice note: presign, PUT, POST /audio-notes (utils/audioNotes.js).
  uploadAudio = uploadAudioNote,
  // One radiometric file: hash, PUT, POST /thermal/captures (utils/thermal/upload.js).
  uploadThermalFile = uploadThermal,
  // A small copy of each photo, for the gallery's cards (0053 thumb_key).
  thumbnail = thumbnailOf,
  session = () => readSession(),
  online = () => typeof navigator === 'undefined' || navigator.onLine !== false,
  now = () => new Date(),
  locks = (typeof navigator !== 'undefined' ? navigator.locks : undefined),
  tabId = newUuid(),
  locationWaitMs = LOCATION_WAIT_MS,
  cameraHoldMs = CAMERA_HOLD_MS,
  photoPasses = PHOTO_PASSES,
  serverPasses = SERVER_PASSES,
  serverRequeueMs = SERVER_REQUEUE_MS,
  leaseMs = LEASE_MS,
  retryBaseMs = 2000,
  // The server this app talks to now, recorded on each record it makes: a
  // record made through another address is then named as such.
  apiBase = () => getApiBase(),
  // The 30 days of weather before a collection, asked by this phone
  // (utils/weather/openMeteo.js): the Lambda has no internet access.
  weatherWindow = fetchWeatherWindow,
} = {}) {
  const listeners = new Set();
  let state = { phase: 'idle', current: null };
  let running = null;
  let again = false;
  let requeueNow = false;
  let attempt = 0;
  let timer = null;
  let timerAt = Infinity;
  let started = false;

  const emit = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((fn) => { try { fn(state); } catch { /* a listener's own problem */ } });
  };

  /** Run again in `delayMs`, unless a run is already due sooner. */
  const schedule = (delayMs) => {
    const wait = Math.max(0, delayMs);
    const at = Date.now() + wait;
    if (timer && timerAt <= at) return;
    if (timer) clearTimeout(timer);
    timerAt = at;
    timer = setTimeout(() => { timer = null; timerAt = Infinity; run(); }, wait);
  };

  const lease = () => new Date(Date.now() + leaseMs).toISOString();

  /* `work(exclusive)` while holding this person's outbox lock, where the
   * browser has one; `busy()` when another tab holds it. Without a lock API,
   * the per-batch claims are what keep two tabs apart. */
  const exclusively = (userId, work, busy) => {
    if (!locks || typeof locks.request !== 'function') return work(false);
    return locks.request(`${LOCK_NAME}:${userId}`, { ifAvailable: true },
      lock => (lock ? work(true) : busy()));
  };

  async function placeName(location) {
    try {
      const r = await client.get('/regions/at', { params: { lat: location.lat, lng: location.lng }, ...REQUEST });
      return r.data?.name_he || r.data?.name_en || '';
    } catch (e) {
      if (isUnreachable(e)) throw e;
      return '';   // no place by that name: not worth holding the specimen for
    }
  }

  async function sendBatch(initial, s, exclusive) {
    const at = () => now().getTime();
    // Claimed and read afresh in one step: what the list showed when this
    // pass began may have been corrected since, or taken by another tab.
    let claim = 'gone';
    let batch = await store.update(s.userId, initial.id, (current) => {
      if (current.status !== 'pending' && current.status !== 'sending') {
        claim = 'moved';
        return null;
      }
      if (!exclusive && current.status === 'sending' && current.sendingBy !== tabId && leaseActive(current)) {
        claim = 'elsewhere';
        return null;
      }
      const held = Date.parse(current.holdUntil || '');
      if (Number.isFinite(held) && held > at()) {
        claim = 'held';
        return null;
      }
      if (!current.location) {
        claim = 'noLocation';
        return null;
      }
      claim = 'mine';
      return { ...current, status: 'sending', sendingBy: tabId, leaseUntil: lease() };
    });
    if (!batch) return { result: 'gone' };
    if (claim === 'moved') return { result: 'skipped' };
    if (claim === 'elsewhere') return { result: 'elsewhere' };
    if (claim === 'held') return { result: 'wait', until: Date.parse(batch.holdUntil) };

    const save = async (patch) => {
      const next = await store.update(s.userId, batch.id, (current) => {
        const proposed = typeof patch === 'function' ? patch(current) : { ...current, ...patch };
        // A batch being sent says so, renewed with every step; one that is
        // not carries no claim.
        return proposed.status === 'sending'
          ? { ...proposed, sendingBy: tabId, leaseUntil: lease() }
          : { ...proposed, sendingBy: undefined, leaseUntil: undefined };
      });
      if (!next) throw new Gone();
      batch = next;
      return next;
    };
    const markPhoto = (id, patch) => save(current => ({
      ...current, photos: (current.photos || []).map(p => (p.id === id ? { ...p, ...patch } : p)),
    }));
    const markAudio = (id, patch) => save(current => ({
      ...current, audioNotes: (current.audioNotes || []).map(a => (a.id === id ? { ...a, ...patch } : a)),
    }));
    const markThermal = (id, patch) => save(current => ({
      ...current, thermal: (current.thermal || []).map(f => (f.id === id ? { ...f, ...patch } : f)),
    }));
    const markBundle = (id, patch) => save(current => ({
      ...current, bundles: (current.bundles || []).map(b => (b.id === id ? { ...b, ...patch } : b)),
    }));
    const markAnnotations = (photoId, patch) => save(current => ({
      ...current, annotationsDone: { ...(current.annotationsDone || {}), [photoId]: patch },
    }));
    // Before every request: still this person, and still signed in for long
    // enough to see it through.
    const guard = (marginMs = 0) => {
      const current = session();
      if (!current || current.userId !== s.userId || current.viewAs) throw new SessionEnded();
      if (marginMs && current.exp * 1000 - Date.now() < marginMs) throw new SessionEnded();
    };

    let created = null;
    let sentBody = null;
    let typed = '';
    /* Whether the record this batch made is still on the server: asked once,
     * after a refusal that may mean it is not (mayBeGone). A 404 throws
     * RecordGone; nobody answering is the usual "wait for a connection". */
    const checkGone = async (status) => {
      if (!created || !mayBeGone(status)) return;
      guard();
      try {
        await client.get(`/collections/${created}`, REQUEST);
      } catch (e) {
        if (isUnreachable(e)) throw e;
        if (e?.response?.status === 404) throw new RecordGone(goneOf(e, now()));
      }
    };
    try {
      // A camera batch is never minted with an unknown place. The camera
      // screen attaches a fix if it finds one in time (offerFix); after that
      // the collector sets it on the map.
      if (claim === 'noLocation') {
        const finished = Date.parse(batch.finalizedAt || batch.createdAt || '');
        const until = (Number.isFinite(finished) ? finished : at()) + locationWaitMs;
        if (batch.source === 'camera' && at() < until) return { result: 'wait', until };
        await save({ status: 'attention', attention: { reason: 'location' } });
        return { result: 'attention' };
      }

      const details = batch.details || {};
      // One key per batch, kept with it, and every body sent under it.
      const hadKey = !!batch.createKey;
      const key = batch.createKey || `field-${newUuid()}`;
      const moment = captureMoment(batch, now());
      const collectedAt = localIsoWithOffset(moment);
      if (!hadKey) await save({ createKey: key });

      created = batch.serverSampleId || null;
      typed = normaliseTag(details.label);
      let code = typed || (details.observeOnly ? '' : (batch.specimenCode || ''));
      // A record already made keeps the code it was made with.
      if (created && batch.serverCode !== undefined) code = batch.serverCode || '';

      if (!created && batch.region === undefined) {
        guard();
        await save({ region: await placeName(batch.location) });
      }

      if (!created && !code && !details.observeOnly && can({ roles: s.roles }, 'specimen:register')) {
        // Asked once and kept, then sent again word for word.
        const mintRequest = batch.mintRequest || mintBody(batch, moment);
        const mintKey = batch.mintKey || `${key}-mint`;
        if (!batch.mintRequest) await save({ mintRequest, mintKey });
        let minted = null;
        try {
          guard();
          minted = (await client.post('/specimens', mintRequest,
            { headers: { 'Idempotency-Key': mintKey }, ...REQUEST })).data;
        } catch (e) {
          // Anything that does not say the mint was refused may yet have made
          // a code: no going on without one.
          if (mintUnsettled(e)) throw e;
        }
        code = minted?.specimen_code || '';
        await save(code
          ? { specimenCode: code, specimenId: minted.specimen_id || null, mintRefused: false }
          // Refused: saved without a code, which can be registered from the
          // collection afterwards.
          : { mintRefused: true });
      }

      if (!created) {
        const body = createBody(batch, code, collectedAt);
        const config = { headers: { 'Idempotency-Key': key }, ...REQUEST };
        const known = hadKey ? sentUnder(batch) : [];
        // Kept BEFORE it goes, beside everything sent under this key before:
        // whichever of them landed, its answer can be asked for again.
        if (!known.some(b => sameRequest(b, body))) await save({ sentBodies: [...known, body], createBody: body });
        sentBody = body;
        let res;
        try {
          guard();
          res = await client.post('/collections', body, config);
        } catch (e) {
          if (e?.response?.status !== 422) throw e;
          // 422: the key answered a different request -- one sent before,
          // which may well have made the record before something was edited.
          // Asked again in its own words, it gives that answer back.
          for (const before of known.filter(b => !sameRequest(b, body)).reverse()) {
            try {
              guard();
              res = await client.post('/collections', before, config);
              sentBody = before;
              break;
            } catch (again422) {
              if (again422?.response?.status !== 422) throw again422;
            }
          }
          if (!res) throw new KeyLost();
        }
        created = res.data.sample_id || res.data.id;
        code = (res.data.qr_uid ?? sentBody.qr_uid) || '';
        const made = { serverSampleId: created, serverCode: code, serverErrors: 0, createBody: sentBody,
                       serverBase: apiBase() };
        if (!sameRequest(sentBody, body)) {
          // The record is the earlier request's. What was corrected since
          // follows it as corrections; what makes up its code cannot, and
          // the card says what the record holds.
          const { changes, ident: wanted, notApplied } = editsBetween(sentBody, body);
          // A name chosen from the AI's answer is recorded by the link below,
          // as an 'ai_image' identification: not a second time as the
          // collector's own 'field' one.
          const accept = batch.ai?.aiId && !batch.aiLinked ? aiLinkBody(batch, created).accept : null;
          const ident = wanted && accept && wanted.taxonomic_rank === accept.rank
            && text(wanted.taxon_name)?.toLowerCase() === text(accept.taxon_name)?.toLowerCase() ? null : wanted;
          Object.assign(made, {
            pendingEdits: changes || ident ? { changes, ident } : null,
            editsNotApplied: notApplied.length ? notApplied : undefined,
            startedAt: sentBody.collection_datetime || batch.startedAt,
            location: Number.isFinite(Number(sentBody.gps_lat)) && sentBody.gps_lat !== undefined
              ? { lat: Number(sentBody.gps_lat), lng: Number(sentBody.gps_lng), accuracy_m: null }
              : batch.location,
            ...(sentBody.region !== undefined ? { region: sentBody.region } : {}),
          });
        }
        await save(made);
      }

      // Corrections that could not go in the request that made the record.
      if (batch.pendingEdits) {
        const { changes, ident } = batch.pendingEdits;
        const refused = [];
        if (changes) {
          try {
            guard();
            await client.put(`/collections/${created}`, changes, REQUEST);
          } catch (e) {
            if (!refusedOutright(e)) throw e;
            await checkGone(e.response.status);
            refused.push(...Object.keys(changes));
          }
        }
        if (ident) {
          try {
            guard();
            await client.post(`/collections/${created}/identifications`, ident,
              { headers: { 'Idempotency-Key': `${key}-ident` }, ...REQUEST });
          } catch (e) {
            if (!refusedOutright(e)) throw e;
            await checkGone(e.response.status);
            refused.push('current_taxon_name');
          }
        }
        await save(current => ({
          ...current,
          pendingEdits: null,
          ...(refused.length ? { editsNotApplied: [...new Set([...(current.editsNotApplied || []), ...refused])] } : {}),
        }));
      }

      // The AI's answer asked for in the form (components/field/
      // AiFieldSuggestion.js): its prediction is linked to the record, and a
      // name the collector accepted and kept becomes their identification,
      // method ai_image (POST /ai/identifications/{id}/link, routes/ai.py).
      // Refused for what it carries (not theirs, already elsewhere, a name the
      // model did not give): the record stands without it.
      if (batch.ai?.aiId && !batch.aiLinked) {
        try {
          guard();
          await client.post(`/ai/identifications/${batch.ai.aiId}/link`, aiLinkBody(batch, created),
            { headers: { 'Idempotency-Key': `${key}-ai` }, ...REQUEST });
        } catch (e) {
          if (!refusedOutright(e)) throw e;
          await checkGone(e.response.status);
        }
        await save({ aiLinked: true });
      }

      // The phenotype the quick-capture screen recorded (a legacy draft), or the answers to the camera's field trait
      // checks (utils/ai/fieldTraits.js).
      if (batch.phenotype && !batch.phenotypeSent) {
        try {
          guard();
          await client.put(`/collections/${created}/phenotype`, batch.phenotype, REQUEST);
        } catch (e) {
          // As that screen did: not worth holding the specimen for -- unless
          // nobody answered, when it goes with the next try.
          if (isUnreachable(e) || e instanceof SessionEnded) throw e;
          await checkGone(e?.response?.status);
        }
        await save({ phenotypeSent: true });
      }

      // Every photo not yet on the record, each marked as it lands, so a
      // retry sends only what is still missing.
      const photos = (batch.photos || []).filter(p => !p.sent && !p.skipped);
      let failures = 0;
      // The last refusal a photo or voice note met: one that may mean the
      // record is gone is looked into before the pass is counted.
      let refusal = null;
      for (const photo of photos) {
        guard(PHOTO_MARGIN_MS);
        const blob = await store.getBlob(s.userId, photo.id);
        if (!blob) {
          // Gone from the device (its storage was cleared): nothing to send.
          await markPhoto(photo.id, { skipped: true, lost: true });
          continue;
        }
        const index = batch.photos.findIndex(p => p.id === photo.id);
        const file = asFile(blob, photo, index);
        const tags = captureFields(photo);
        // Made once per photo, and only when the file still has to go up.
        let small = null;
        if (!(batch.photos[index] || photo).s3_key) {
          // A browser that cannot decode it says so, or says nothing: never
          // held up for more than a few seconds.
          let gaveUp;
          const made = await Promise.race([
            Promise.resolve().then(() => thumbnail(blob)).catch(() => null),
            new Promise((done) => { gaveUp = setTimeout(() => done(null), 10 * 1000); }),
          ]);
          clearTimeout(gaveUp);
          small = blobOfDataUrl(made);
        }
        const send = (tagged) => {
          const current = (batch.photos || []).find(p => p.id === photo.id) || photo;
          return uploadOne(
            { file, photoType: photo.view, notes: photo.note || '' },
            { sample_id: created, ...(tagged ? tags : {}) },
            (details.taxon_name || '').trim(),
            'field_collection',
            {
              // Put in storage once: a retry registers the same file.
              // With its small copy's key, if it had one: the registration
              // is the same request every time, or the key answers 422.
              stored: current.s3_key ? storedOf(current) : null,
              onStored: where => markPhoto(photo.id, where),
              idempotencyKey: photoKey(key, photo.id, tagged),
              requestConfig: REQUEST,
              thumbnail: small,
            },
          );
        };
        let result = await send(true);
        const untagged = !result.ok && TAG_REFUSALS.has(result.code) && Object.keys(tags).length > 0;
        if (untagged) {
          guard(PHOTO_MARGIN_MS);
          result = await send(false);
        }
        if (!result.ok) {
          // Nobody answered: the rest would go the same way, and nothing was
          // refused. It waits for a connection, photos and all.
          if (result.unreachable || !online()) throw unreachableError();
          if (result.status === 401) throw new SessionEnded();
          // Not a verdict on the photo: the app and the server are mid-deploy
          // on different versions (426), or the server asked to slow down
          // (429). The batch waits as a whole, as for any other step, and
          // the photo is not counted as failed -- three such passes used to
          // ask the collector to go on without photos nobody had refused.
          if (result.status === 426 || result.status === 429) {
            throw Object.assign(new Error('not a verdict on the photo'),
              { response: { status: result.status, data: {} } });
          }
          failures += 1;
          refusal = result.status;
          continue;
        }
        // The server's id is kept: a tissue outline drawn on this photo is
        // sent under it (below).
        const served = result.photo_id ? { serverId: result.photo_id } : {};
        await markPhoto(photo.id, untagged ? { sent: true, tagsRefused: true, ...served } : { sent: true, ...served });
      }

      // Then the Z-stacks (docs/FIELD_AI_VISION_SPEC.md 10.3): once every
      // layer of a stack has gone up or been let go, the photographs that
      // went up are tied together (POST /media-bundles). The same layers
      // again are the same bundle on the server, so a retry needs no key.
      // Refused outright, or with fewer than two layers there, it is let go:
      // the record never waits for it. A layer removed in the editor is
      // simply not in it.
      for (const bundle of (batch.bundles || []).filter(b => !b.sent && !b.skipped)) {
        const layers = bundle.stack.layers.map(l => (batch.photos || []).find(p => p.id === l.photoId) || null);
        if (layers.some(p => p && !p.sent && !p.skipped)) continue;
        const body = bundleBody(bundle.stack, layers.map(p => (p?.sent && p.serverId) || null),
          { medium: 'photo_id', viewport: 'field', device: bundle.stack.device });
        if (!body) {
          await markBundle(bundle.id, { skipped: true, refused: { status: null, code: 'too_few_layers' } });
          continue;
        }
        guard();
        try {
          const answer = await client.post('/media-bundles', body, REQUEST);
          await markBundle(bundle.id, { sent: true, bundleId: answer?.data?.bundle_id || null });
        } catch (e) {
          if (!refusedOutright(e)) throw e;
          await markBundle(bundle.id, { skipped: true,
            refused: { status: e.response.status, code: e.response.data?.code || null } });
        }
      }

      // Then the voice notes, the same way: each marked as it lands, under a
      // key of its own, put in storage once, one gone from the device let
      // go. A note whose recorded-at the server refuses (a phone whose clock
      // runs fast) goes again without it.
      const notes = (batch.audioNotes || []).filter(a => !a.sent && !a.skipped);
      let audioFailures = 0;
      for (const note of notes) {
        guard(PHOTO_MARGIN_MS);
        const blob = await store.getBlob(s.userId, note.id);
        if (!blob) {
          await markAudio(note.id, { skipped: true, lost: true });
          continue;
        }
        const send = (timed) => {
          const current = (batch.audioNotes || []).find(a => a.id === note.id) || note;
          return uploadAudio(
            { blob, contentType: note.contentType, durationS: note.durationS,
              recordedAt: timed ? note.recordedAt : null },
            { sample_id: created },
            {
              stored: current.s3_key ? { s3_key: current.s3_key, s3_url: current.s3_url } : null,
              onStored: where => markAudio(note.id, where),
              idempotencyKey: audioKey(key, note.id, timed),
              requestConfig: REQUEST,
            },
          );
        };
        let result = await send(true);
        const untimed = !result.ok && result.code === 'invalid_recorded_at' && !!note.recordedAt;
        if (untimed) {
          guard(PHOTO_MARGIN_MS);
          result = await send(false);
        }
        if (!result.ok) {
          if (result.unreachable || !online()) throw unreachableError();
          if (result.status === 401) throw new SessionEnded();
          if (result.status === 426 || result.status === 429) {
            throw Object.assign(new Error('not a verdict on the voice note'),
              { response: { status: result.status, data: {} } });
          }
          audioFailures += 1;
          refusal = result.status;
          continue;
        }
        await markAudio(note.id, untimed ? { sent: true, timeRefused: true } : { sent: true });
      }

      // Then the thermal files (docs/FIELD_AI_VISION_SPEC.md section 42), each
      // as the camera's app saved it, linked to the collection. The same file
      // twice is one capture on the server, so a retry needs no key. One no
      // decoder reads is still sent: kept 'undecoded', to be decoded later.
      // Refused outright (an account that may not add thermal images, a file
      // too large) it is let go, said on the item: the record never waits
      // for it. Nobody answering, or the server's own trouble, is as for a
      // photo.
      const thermalFiles = (batch.thermal || []).filter(f => !f.sent && !f.skipped);
      let thermalFailures = 0;
      for (const item of thermalFiles) {
        guard(PHOTO_MARGIN_MS);
        const blob = await store.getBlob(s.userId, item.id);
        if (!blob) {
          await markThermal(item.id, { skipped: true, lost: true });
          continue;
        }
        const result = await uploadThermalFile(blob, {
          contentType: item.contentType, links: [{ kind: 'sample', id: created }],
          lidState: item.lidState || null, requestConfig: REQUEST,
        });
        if (!result.ok) {
          if (result.unreachable || !online()) throw unreachableError();
          if (result.status === 401) throw new SessionEnded();
          if (result.status === 426 || result.status === 429) {
            throw Object.assign(new Error('not a verdict on the thermal file'),
              { response: { status: result.status, data: {} } });
          }
          if (result.status && result.status < 500 && result.status !== 404 && result.status !== 408) {
            await markThermal(item.id, { skipped: true, refused: { status: result.status, code: result.code || null } });
            continue;
          }
          thermalFailures += 1;
          refusal = result.status;
          continue;
        }
        await markThermal(item.id, { sent: true, captureId: result.capture?.capture_id || null,
          decoded: result.decoded, refusal: result.refusal?.code || null });
      }

      if (failures || audioFailures || thermalFailures) {
        await checkGone(refusal);
        // Submitting now would close the record without them. It stays a
        // draft on the server until they arrive or the collector goes on
        // without them.
        const passes = (batch.photoPasses || 0) + 1;
        if (passes >= photoPasses) {
          let attention = { reason: 'thermal', count: thermalFailures };
          if (failures) attention = { reason: 'photos', count: failures };
          else if (audioFailures) attention = { reason: 'audio', count: audioFailures };
          await save({ photoPasses: passes, status: 'attention', attention });
          return { result: 'attention' };
        }
        await save({ photoPasses: passes, status: 'pending' });
        return { result: 'retry', tries: passes };
      }

      // Then the Annotations step (docs/UI_HUBS_AND_GESTURES_PLAN.md section
      // 5): the summary's tissue outline (docs/GAME_DESIGN.md Part III
      // section 18) and the Pro drawer's marks, merged into ONE
      // PUT /photos/{id}/annotations per photograph (utils/fieldAnnotations.js:
      // the PUT replaces the person's whole set, so two would leave only the
      // second), under the photo's server id. The body is made from what is
      // stored, so a retry sends the same one; each photograph is marked as
      // it lands. Marks on a photograph that never went up (let go, lost, or
      // sent without by the collector), marks the server would refuse, and a
      // PUT refused outright (a guest or a read-only account may not
      // annotate) are let go, said on the batch: the record never waits for
      // them. Nobody answering, or the server's own trouble, is as for any step.
      for (const photoId of annotatedPhotoIds(batch)) {
        if (batch.annotationsDone?.[photoId]) continue;
        const target = (batch.photos || []).find(p => p.id === photoId);
        if (!target?.sent || !target.serverId) {
          await markAnnotations(photoId, { dropped: 'photo_not_sent' });
          continue;
        }
        const { body, problem } = annotationBody(batch, photoId);
        if (!body) {
          await markAnnotations(photoId, { dropped: problem || 'invalid' });
          continue;
        }
        guard();
        try {
          await client.put(`/photos/${target.serverId}/annotations`, body, REQUEST);
          await markAnnotations(photoId, { sent: true });
        } catch (e) {
          if (!refusedOutright(e)) throw e;
          await checkGone(e.response.status);
          await markAnnotations(photoId, { dropped: 'refused', status: e.response.status,
            code: e.response.data?.code || null });
        }
      }

      // The 30 days of weather before the collection (Part I section 4 of
      // docs/FIELD_AI_VISION_SPEC.md): asked of Open-Meteo by this phone with
      // the position rounded to about a kilometre, written as source 'device'.
      // Best effort, and never a reason to hold the specimen back: a refusal
      // or an empty answer is settled for good; no answer at all (offline,
      // their service down) is simply tried again if this batch goes round
      // again.
      if (!batch.weatherDone && batch.location && online()) {
        let settled = null;
        try {
          const { body } = await weatherWindow(batch.location, moment, { now: now() });
          if (!body) settled = 'empty';
          else {
            guard();
            await client.put(`/collections/${created}/weather`, body, REQUEST);
            settled = 'sent';
          }
        } catch (e) {
          if (e instanceof SessionEnded) throw e;
          const status = e?.status ?? e?.response?.status;
          // Their refusal of the question, or ours of the answer (an
          // expert's record kept, 409): nothing to try again. A session that
          // ended, a version being deployed or a rate limit is not an answer.
          if (WEATHER_SETTLED.has(status)) settled = `refused_${status}`;
        }
        if (settled) await save({ weatherDone: settled });
      }

      // Field Mode's own completeness rule (collections.py submit_collection).
      guard();
      try {
        await client.put(`/collections/${created}/submit`, { mode: 'field' }, REQUEST);
      } catch (e) {
        // A submit that landed while its answer was lost is refused as
        // already submitted when asked again; the record says which.
        await checkGone(e?.response?.status);
        if (e?.response?.status !== 400) throw e;
        const record = await client.get(`/collections/${created}`, REQUEST).catch(() => null);
        if (record?.data?.is_draft !== false) throw e;
      }

      // A pickup or drop-off asked for in the field (B6: the live prompt, or
      // the editor's form), sent once the record is: the server takes a
      // request only for a sent record. Refused (the find already has one,
      // say): kept on the batch as said, the record stands without it. Read
      // afresh first: the prompt may have been answered while this pass ran.
      await save(current => current);
      if (batch.handoff && !batch.handoffSent) {
        // The answer that prompted it, while it is this record's: linked
        // above, or a request asked for without the AI.
        const aiId = batch.handoff.aiId && batch.aiLinked && batch.ai?.aiId === batch.handoff.aiId
          ? batch.handoff.aiId : null;
        // Each request under a key of its own (withHandoffRequest): a request
        // changed after an earlier one was answered never meets that answer.
        const handoffKey = batch.handoffKey || key;
        let refused = null;
        for (const [ask, suffix] of [[aiId, 'handoff'], [null, 'handoff-plain']]) {
          try {
            guard();
            await client.post('/handoffs', handoffBody(batch.handoff, created, { aiId: ask }),
              { headers: { 'Idempotency-Key': `${handoffKey}-${suffix}` }, ...REQUEST });
            refused = null;
            break;
          } catch (e) {
            // A 422 is the idempotency layer's: the key holds another request
            // this phone has no copy of. The step is optional, so it settles
            // as refused rather than hold the record. A 429 (slowed down) is
            // not kept under the key: the batch waits and asks again with it.
            const keyHeld = e?.response?.status === 422;
            if (!keyHeld && !refusedOutright(e)) throw e;
            await checkGone(e.response.status);
            refused = { status: e.response.status, code: e.response.data?.code || null };
            // The answer was refused, not the request: asked again without it.
            if (keyHeld || !ask || refused.code !== 'invalid_ai_id') break;
          }
        }
        // Only the request that was sent: one changed or taken back while it
        // was on its way is the next pass's (its own key, or none).
        await save(current => (current.handoffKey === batch.handoffKey
          ? { ...current, handoffSent: true, handoffRefused: refused } : current));
      }
      // A request written since the step read the batch sends the batch round
      // once more (released, at once) rather than be left on a sent one.
      await save(current => (current.handoff && !current.handoffSent
        ? { ...current, status: 'pending' }
        : { ...current, status: 'sent', submitted: true, sentAt: now().toISOString(), attention: null,
            photoPasses: 0, serverErrors: 0 }));
      if (batch.status !== 'sent') return { result: 'retry', delay: 0 };
      // Only what the server now has. A photo (or voice note) the collector
      // chose to go without is not on the server: it stays here, to be sent
      // later from the record (FieldSentEditor) or let go with the batch.
      await store.removeBlobs(s.userId, [...(batch.photos || []), ...(batch.audioNotes || []), ...(batch.thermal || [])]
        .filter(p => p.sent).map(p => p.id));
      return { result: 'sent' };
    } catch (e) {
      if (e instanceof Gone) return { result: 'gone' };
      try {
        return await settle(e);
      } catch (inner) {
        if (inner instanceof Gone) return { result: 'gone' };
        throw inner;
      }
    }

    async function settle(e) {
      const status = e?.response?.status;
      if (e instanceof RecordGone) {
        // Nothing to retry: no request will bring the record back. The
        // record page (FieldSentEditor) says why and offers the way on --
        // restore it, send it again as a new record, or let the phone's copy go.
        await save({ status: 'attention', attention: { reason: 'gone', ...e.gone }, gone: e.gone });
        return { result: 'attention' };
      }
      if (e instanceof SessionEnded || status === 401) {
        await save({ status: 'pending' });
        return { result: 'signedOut' };
      }
      if (e instanceof KeyLost) {
        // Keys kept: whatever the key holds, the next try asks it again.
        await save({ status: 'attention', attention: { reason: 'server', at: now().toISOString() } });
        return { result: 'attention' };
      }
      if (isUnreachable(e)) {
        await save({ status: 'pending' });
        return { result: 'unreachable' };
      }
      if (stillRunning(e)) {
        // The first request may yet land: the key stays.
        await save({ status: 'pending' });
        return { result: 'retry', tries: 1 };
      }
      if (status === 426) {
        // The app and the server are on different versions: see OUTDATED_RETRY_MS.
        await save({ status: 'pending' });
        return { result: 'retry', delay: OUTDATED_RETRY_MS };
      }
      if (status === 429) {
        // Slowed down, nothing done. The server keeps no 429 under its key, but
        // a request that has not made a record still goes next time as a new one.
        const keys = !created
          ? { createKey: undefined, createBody: undefined, sentBodies: undefined, mintRequest: undefined, mintKey: undefined }
          : {};
        await save({ ...keys, status: 'pending' });
        const retryAfter = Number(e.response.data?.retry_after) * 1000;
        return { result: 'retry', delay: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60 * 1000 };
      }
      // The create was refused, so nothing was written: its key goes, and
      // the next try is a new request carrying whatever was corrected. The
      // code minted for it stays, so no second one is minted.
      const forget = !created && refusedOutright(e)
        ? { createKey: undefined, createBody: undefined, sentBodies: undefined, mintRequest: undefined, mintKey: undefined }
        : {};
      const holder = e?.response?.data?.code;
      if (!created && typed && sentBody?.qr_uid === typed && status === 409
          && (holder === 'code_taken' || holder === 'code_reserved')) {
        // The label belongs to another record (a deleted one still holds its
        // code): the collector chooses another.
        await save({ ...forget, status: 'attention', attention: { reason: 'label', code: holder, label: typed } });
        return { result: 'attention' };
      }
      if (!status || status >= 500) {
        // The server's own trouble: tried again, with the key kept (a 504
        // says nothing about whether the request landed).
        const errors = (batch.serverErrors || 0) + 1;
        if (errors >= serverPasses) {
          await save({ ...forget, serverErrors: errors, status: 'attention',
                       attention: { reason: 'server', at: now().toISOString() } });
          schedule(serverRequeueMs);
          return { result: 'attention' };
        }
        await save({ ...forget, serverErrors: errors, status: 'pending' });
        return { result: 'retry', tries: errors };
      }
      await save({
        ...forget,
        status: 'attention',
        // The code is what the screen explains in the collector's language;
        // the server's sentence is kept only as a detail.
        attention: { reason: created ? 'submit' : 'refused', status, code: holder || null,
                     message: e.response?.data?.error || null },
      });
      return { result: 'attention' };
    }
  }

  /* Batches the server kept failing on go back in the queue: at once when the
   * connection or the page comes back, else once they have waited a while. */
  async function requeueServerErrors(userId, everyOne) {
    let due = Infinity;
    for (const b of await store.list(userId)) {
      if (b.status !== 'attention' || b.attention?.reason !== 'server') continue;
      const since = Date.parse(b.attention.at || '');
      const ready = everyOne || !Number.isFinite(since) || now().getTime() - since >= serverRequeueMs;
      if (!ready) {
        due = Math.min(due, since + serverRequeueMs - now().getTime());
        continue;
      }
      await store.update(userId, b.id, current => (
        current.status === 'attention' && current.attention?.reason === 'server'
          ? { ...current, status: 'pending', attention: null, serverErrors: 0 }
          : null));
    }
    if (Number.isFinite(due)) schedule(due);
  }

  async function sendAll(s, exclusive) {
    const everyOne = requeueNow;
    requeueNow = false;
    await requeueServerErrors(s.userId, everyOne);
    // `sending` too: a tab closed mid-send left it so.
    const batches = (await store.list(s.userId))
      .filter(b => b.status === 'pending' || b.status === 'sending')
      .sort((a, b) => String(a.finalizedAt || a.createdAt).localeCompare(String(b.finalizedAt || b.createdAt)));
    for (const batch of batches) {
      emit({ phase: 'sending', current: batch.id });
      const outcome = await sendBatch(batch, s, exclusive);
      if (outcome.result === 'sent') attempt = 0;
      if (outcome.result === 'unreachable') {
        // Nobody answered: the rest would go the same way.
        attempt += 1;
        schedule(backoffMs(attempt, { base: retryBaseMs }));
        return emit({ phase: 'offline', current: null });
      }
      if (outcome.result === 'signedOut') return emit({ phase: 'signedOut', current: null });
      if (outcome.result === 'retry') {
        schedule(outcome.delay ?? backoffMs(outcome.tries, { base: retryBaseMs }));
      }
      if (outcome.result === 'wait') schedule(outcome.until - now().getTime());
      if (outcome.result === 'elsewhere') schedule(ELSEWHERE_RETRY_MS);
    }
    return emit({ phase: 'idle', current: null });
  }

  async function pass() {
    const s = session();
    if (!s) return emit({ phase: 'signedOut', current: null });
    // Viewing the app as another role: nothing is sent until Exit.
    if (s.viewAs) return emit({ phase: 'viewingAs', current: null });
    if (!online()) return emit({ phase: 'offline', current: null });
    return exclusively(s.userId, exclusive => sendAll(s, exclusive), () => {
      // Another tab of this app is sending this person's batches.
      schedule(ELSEWHERE_RETRY_MS);
      return emit({ phase: 'elsewhere', current: null });
    });
  }

  /* A batch left `sending` by a tab that closed mid-send waits for nobody:
   * once per page, it goes back to `pending` -- whatever the connection --
   * so it can be corrected and is sent by the next pass. Only by the tab
   * that holds the lock (or, with no lock API, once its claim has run out). */
  async function releaseStale() {
    const s = session();
    if (!s || s.viewAs) return;
    await exclusively(s.userId, async (exclusive) => {
      for (const b of await store.list(s.userId)) {
        if (b.status !== 'sending') continue;
        await store.update(s.userId, b.id, current => (
          current.status === 'sending' && (exclusive || !leaseActive(current))
            ? { ...current, status: 'pending', sendingBy: undefined, leaseUntil: undefined }
            : null));
      }
    }, () => {});
  }

  /** Send what is waiting. Runs once at a time; asked while running, it runs
   *  again when done. Resolves when nothing more is to be done now. */
  function run() {
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      try {
        do {
          again = false;
          await pass();
        } while (again);
      } catch {
        // The store would not open or answer: nothing can be sent until the
        // next trigger.
        emit({ phase: 'idle', current: null });
      } finally {
        running = null;
      }
    })();
    return running;
  }

  const onOnline = () => { attempt = 0; requeueNow = true; run(); };
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      requeueNow = true;
      run();
    }
  };

  return {
    run,

    /** How long the camera holds a finished batch back (holdUntil). */
    cameraHoldMs,

    /** Listen for a connection and run. Safe to call again. */
    start() {
      const first = !started;
      if (!started && typeof window !== 'undefined') {
        started = true;
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisible);
      }
      if (!first) return run();
      return releaseStale().catch(() => {}).then(() => run());
    },

    stop() {
      if (started) {
        window.removeEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisible);
        started = false;
      }
      if (timer) clearTimeout(timer);
      timer = null;
      timerAt = Infinity;
    },

    /** A fix from the camera, for batches finished without a place that are
     *  still waiting for one. */
    async offerFix(fix) {
      const s = session();
      if (!s || s.viewAs || !fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return;
      const at = now().getTime();
      let placed = false;
      for (const b of await store.list(s.userId)) {
        const finished = Date.parse(b.finalizedAt || '');
        if (b.status !== 'pending' || b.location || b.source !== 'camera') continue;
        if (Number.isFinite(finished) && at - finished >= locationWaitMs) continue;
        const next = await store.update(s.userId, b.id, x => (x.location ? null : {
          ...x, location: { lat: fix.lat, lng: fix.lng, accuracy_m: Number.isFinite(fix.accuracy_m) ? fix.accuracy_m : null },
        }));
        if (next?.location) placed = true;
      }
      if (placed) run();
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    getState: () => state,

    /** Whether a request may be made now: the screens ask before reading
     *  the server, for the same reason the runner does. */
    signedIn: () => !!session(),
  };
}

let shared = null;
/** The page's one outbox. */
export function fieldOutbox() {
  shared = shared || createOutbox();
  return shared;
}
